import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, Row, Col, Spinner, Alert, ListGroup, Form, Button, Modal } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'; // ייבוא רכיבי גרפים

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import EventForm from './EventForm';
import { API_BASE_URL, buildApiUrl } from '../config/api';
import { Event } from '../interfaces';
import DefectReportGeneralModal from './DefectReportGeneralModal';

// ממשקים (נלקחים מ-interfaces.ts אך מוגדרים כאן לנוחות הקריאה)
interface LowStockConsumable {
  id: number;
  name: string;
  current_stock: number;
  min_stock_level: number;
  unit: string | null;
}

interface DashboardSummary {
  equipment_summary: {
    total_equipment: number;
    defective_equipment: number;
    available_equipment: number;
    equipment_in_use_count: number;
  };
  event_summary: {
    total_events: number;
    events_in_preparation: number;
    active_events: number;
    completed_events_today: number;
  };
  consumables_summary: {
    total_consumables: number;
    low_stock_count: number;
    low_stock_items: LowStockConsumable[];
  };
  advanced_reports: { // ממשק עבור דוחות מתקדמים
    defective_by_category: { subcategory__category__category_name: string; count: number }[];
    equipment_in_use_report: {
      equipment_usage_data: { equipment__name: string; total_quantity_used: number }[];
      rented_usage_data: { rented_item_name: string; total_quantity_rented: number }[];
      total_in_use_count: number;
    };
  };
}

interface DefectHistoryRecord {
  equipment: number;
  quantity: number;
  priority?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTechnician = user?.role?.name === 'TECHNICIAN';
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [defectiveSummary, setDefectiveSummary] = useState<{
    reportsCount: number;
    affectedEquipmentCount: number;
    defectiveUnitsCount: number;
    criticalReportsCount: number;
  }>({
    reportsCount: 0,
    affectedEquipmentCount: 0,
    defectiveUnitsCount: 0,
    criticalReportsCount: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showEventFormForNewEvent, setShowEventFormForNewEvent] = useState<boolean>(false);
  const [initialNewEventDates, setInitialNewEventDates] = useState<{ startDate: Date, endDate: Date } | null>(null);
  const [showDefectReportModal, setShowDefectReportModal] = useState<boolean>(false);

  // מצבי תאריכים עבור דוח "ציוד בשימוש"
  const [reportStartDate, setReportStartDate] = useState<string>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) // לפני שבוע
  );
  const [reportEndDate, setReportEndDate] = useState<string>(
    new Date().toISOString().slice(0, 10) // היום
  );

  const handleAddEventClick = () => {
    setInitialNewEventDates({ 
      startDate: new Date(), 
      endDate: new Date(Date.now() + 24 * 60 * 60 * 1000) // מחר
    });
    setShowEventFormForNewEvent(true);
  };

  const handleNewEventFormSuccess = (createdEvent: Event) => {
    setShowEventFormForNewEvent(false);
    setInitialNewEventDates(null);
    // מעבר ישירות לעמוד פרטי האירוע החדש
    if (createdEvent.id) {
      navigate(`/event-details/${createdEvent.id}`);
    } else {
      // פול-בק: אם אין מזהה, נשאר עם הרענון הקודם
      window.location.reload();
    }
  };

  const handleNewEventFormCancel = () => {
    setShowEventFormForNewEvent(false);
    setInitialNewEventDates(null);
  };

  const handleDefectReport = async (_defectData: any) => {
    /* הודעת הצלחה מוצגת ב-DefectReportGeneralModal */
  };

  useEffect(() => {
    if (isTechnician) {
      setShowDefectReportModal(false);
    }
  }, [isTechnician]);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: { [key: string]: any } = {};
        if (reportStartDate) params.report_start_date = reportStartDate;
        if (reportEndDate) params.report_end_date = reportEndDate;

        const [summaryResponse, defectHistoryResponse] = await Promise.all([
          axios.get(buildApiUrl('dashboard/'), { params }),
          axios.get(buildApiUrl('inventory/defect-history/'))
        ]);

        const defectHistory = Array.isArray(defectHistoryResponse.data)
          ? (defectHistoryResponse.data as DefectHistoryRecord[])
          : [];
        const affectedEquipmentIds = new Set(defectHistory.map(record => record.equipment));
        const defectiveUnitsCount = defectHistory.reduce((total, record) => total + (record.quantity || 0), 0);
        const criticalReportsCount = defectHistory.filter(record => record.priority === 'critical').length;

        setSummary(summaryResponse.data);
        // נתוני הציוד התקול נמשכים ישירות ממקור האמת: defect-history.
        setDefectiveSummary({
          reportsCount: defectHistory.length,
          affectedEquipmentCount: affectedEquipmentIds.size,
          defectiveUnitsCount,
          criticalReportsCount
        });
        setLoading(false);
      } catch (err) {
        console.error("שגיאה בטעינת סיכום הדוחות:", err);
        setError("לא ניתן לטעון סיכום דוחות. וודא ששרת Django פועל.");
        setLoading(false);
      }
    };
    fetchSummary();
  }, [reportStartDate, reportEndDate]); // טען מחדש כשמצבי התאריכים משתנים


  if (loading) return <Spinner animation="border" role="status"><span className="visually-hidden">טוען סיכום...</span></Spinner>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!summary) return <Alert variant="info">אין נתונים זמינים לסיכום.</Alert>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>לוח מחוונים</h2>
        <div>
          <Button
            variant="warning"
            onClick={() => setShowDefectReportModal(true)}
            className="me-2"
            disabled={isTechnician}
            title={isTechnician ? 'אין לטכנאי הרשאה לדווח תקלות; רק ראש צוות ומעלה' : ''}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            דיווח על ציוד תקול
          </Button>
          <Button variant="success" onClick={handleAddEventClick}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            הוסף אירוע חדש
          </Button>
        </div>
      </div>
      <Row className="mt-4">
        {/* סיכום ציוד */}
        <Col md={6} lg={4} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">סיכום ציוד</Card.Header>
            <Card.Body>
              <Card.Text><strong>סה"כ ציוד:</strong> {summary.equipment_summary.total_equipment}</Card.Text>
              <Card.Text><strong>דיווחי תקלות:</strong> <span className="text-danger">{defectiveSummary.reportsCount}</span></Card.Text>
              <Card.Text><strong>פריטים עם תקלה:</strong> <span className="text-danger">{defectiveSummary.affectedEquipmentCount}</span></Card.Text>
              <Card.Text><strong>יחידות תקולות:</strong> <span className="text-danger">{defectiveSummary.defectiveUnitsCount}</span></Card.Text>
              <Card.Text><strong>תקלות קריטיות:</strong> <span className={defectiveSummary.criticalReportsCount > 0 ? 'text-danger fw-bold' : ''}>{defectiveSummary.criticalReportsCount}</span></Card.Text>
              <Card.Text><strong>ציוד זמין כרגע:</strong> <span className="text-success">{summary.equipment_summary.available_equipment}</span></Card.Text>
              <Card.Text><strong>ציוד בשימוש כרגע:</strong> {summary.equipment_summary.equipment_in_use_count}</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        {/* סיכום אירועים */}
        <Col md={6} lg={4} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-info text-white">סיכום אירועים</Card.Header>
            <Card.Body>
              <Card.Text><strong>סה"כ אירועים:</strong> {summary.event_summary.total_events}</Card.Text>
              <Card.Text><strong>אירועים בהכנה:</strong> {summary.event_summary.events_in_preparation}</Card.Text>
              <Card.Text><strong>אירועים פעילים:</strong> {summary.event_summary.active_events}</Card.Text>
              <Card.Text><strong>אירועים שהסתיימו היום:</strong> {summary.event_summary.completed_events_today}</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        {/* התראות על מלאי מתכלים נמוך */}
        <Col md={6} lg={4} className="mb-4">
          <Card className={`shadow-sm ${summary.consumables_summary.low_stock_count > 0 ? 'border-danger' : ''}`}>
            <Card.Header className={`text-white ${summary.consumables_summary.low_stock_count > 0 ? 'bg-danger' : 'bg-success'}`}>
              התראות מתכלים
            </Card.Header>
            <Card.Body>
              <Card.Text><strong>סה"כ פריטים מתכלים:</strong> {summary.consumables_summary.total_consumables}</Card.Text>
              <Card.Text>
                <strong>פריטים במלאי נמוך:</strong>{' '}
                <span className={summary.consumables_summary.low_stock_count > 0 ? 'text-danger fw-bold' : ''}>
                  {summary.consumables_summary.low_stock_count}
                </span>
              </Card.Text>
              {summary.consumables_summary.low_stock_count > 0 && (
                <ListGroup variant="flush">
                  {summary.consumables_summary.low_stock_items.map(item => (
                    <ListGroup.Item key={item.id} className="text-danger">
                      {item.name} ({item.current_stock}{item.unit || ''} / {item.min_stock_level}{item.unit || ''})
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
              {summary.consumables_summary.low_stock_count === 0 && (
                <Alert variant="success" className="mt-3">אין התראות מלאי נמוך</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* --- חדש: דוח התפלגות ציוד תקול לפי קטגוריה --- */}
        <Col md={6} lg={6} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-warning text-dark">התפלגות ציוד תקול לפי קטגוריה</Card.Header>
            <Card.Body>
              <Row className="g-2 mb-3 text-center">
                <Col xs={6} md={3}>
                  <div className="border rounded p-2 bg-danger bg-opacity-10">
                    <div className="fw-bold text-danger">{defectiveSummary.reportsCount}</div>
                    <small>דיווחים</small>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="border rounded p-2 bg-warning bg-opacity-10">
                    <div className="fw-bold text-warning">{defectiveSummary.affectedEquipmentCount}</div>
                    <small>פריטים</small>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="border rounded p-2 bg-secondary bg-opacity-10">
                    <div className="fw-bold text-secondary">{defectiveSummary.defectiveUnitsCount}</div>
                    <small>יחידות תקולות</small>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="border rounded p-2 bg-dark bg-opacity-10">
                    <div className="fw-bold text-dark">{defectiveSummary.criticalReportsCount}</div>
                    <small>קריטיות</small>
                  </div>
                </Col>
              </Row>
              {summary.advanced_reports.defective_by_category.length === 0 ? null : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={summary.advanced_reports.defective_by_category}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="subcategory__category__category_name" angle={-45} textAnchor="end" height={80} interval={0} />
                    <YAxis allowDecimals={false} label={{ value: 'מספר פריטים', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#dc3545" name="מספר תקולים" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* --- חדש: דוח ציוד בשימוש בתקופה נבחרת --- */}
        <Col md={6} lg={6} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-secondary text-white">ציוד בשימוש בתקופה נבחרת</Card.Header>
            <Card.Body>
              <Form>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="reportStartDate">
                    <Form.Label>מתאריך</Form.Label>
                    <Form.Control type="date" value={reportStartDate} onChange={(e) => setReportStartDate(e.target.value)} />
                  </Form.Group>
                  <Form.Group as={Col} controlId="reportEndDate">
                    <Form.Label>עד תאריך</Form.Label>
                    <Form.Control type="date" value={reportEndDate} onChange={(e) => setReportEndDate(e.target.value)} />
                  </Form.Group>
                </Row>
              </Form>

              {summary.advanced_reports.equipment_in_use_report.total_in_use_count === 0 ? (
                <Alert variant="info" className="mt-3">אין נתוני שימוש בתקופה זו.</Alert>
              ) : (
                <>
                  <p className="fw-bold">סה"כ פריטים בשימוש: {summary.advanced_reports.equipment_in_use_report.total_in_use_count}</p>
                  <h6 className="mt-3">פירוט ציוד מהמלאי</h6>
                  <ListGroup variant="flush">
                    {summary.advanced_reports.equipment_in_use_report.equipment_usage_data.map((item, index) => (
                      <ListGroup.Item key={`used-eq-${index}`}>
                        {item.equipment__name}: {item.total_quantity_used} יח'
                      </ListGroup.Item>
                    ))}
                  </ListGroup>

                  {summary.advanced_reports.equipment_in_use_report.rented_usage_data.length > 0 && (
                    <>
                      <h6 className="mt-3">פירוט ציוד מושכר חיצוני</h6>
                      <ListGroup variant="flush">
                        {summary.advanced_reports.equipment_in_use_report.rented_usage_data.map((item, index) => (
                          <ListGroup.Item key={`rented-eq-${index}`}>
                            {item.rented_item_name}: {item.total_quantity_rented} יח'
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* --- חדש: סטטוס אירועים לפי חודש --- */}
        <Col md={6} lg={6} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-success text-white">סטטוס אירועים לפי חודש</Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>אירועים בחודש הנוכחי</h6>
                <span className="badge bg-primary fs-6">{summary.event_summary.total_events}</span>
              </div>
              
              <div className="row text-center">
                <div className="col-4">
                  <div className="border rounded p-2 bg-warning bg-opacity-10">
                    <div className="fw-bold text-warning">{summary.event_summary.events_in_preparation}</div>
                    <small>בהכנה</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-2 bg-success bg-opacity-10">
                    <div className="fw-bold text-success">{summary.event_summary.active_events}</div>
                    <small>פעילים</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-2 bg-info bg-opacity-10">
                    <div className="fw-bold text-info">{summary.event_summary.completed_events_today}</div>
                    <small>הושלמו היום</small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* --- חדש: סטטוס ציוד לפי קטגוריה --- */}
        <Col md={6} lg={6} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">סטטוס ציוד לפי קטגוריה</Card.Header>
            <Card.Body>
              <div className="row text-center">
                <div className="col-6">
                  <div className="border rounded p-2 bg-success bg-opacity-10">
                    <div className="fw-bold text-success">{summary.equipment_summary.available_equipment}</div>
                    <small>זמין</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="border rounded p-2 bg-warning bg-opacity-10">
                    <div className="fw-bold text-warning">{summary.equipment_summary.equipment_in_use_count}</div>
                    <small>בשימוש</small>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>ציוד תקול:</span>
                  <span className={`badge ${defectiveSummary.reportsCount > 0 ? 'bg-danger' : 'bg-success'}`}>
                    {defectiveSummary.reportsCount}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>סה"כ ציוד:</span>
                  <span className="badge bg-secondary">{summary.equipment_summary.total_equipment}</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* --- חדש: סטטוס מתכלים לפי קטגוריה --- */}
        <Col md={6} lg={6} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-warning text-dark">סטטוס מתכלים לפי קטגוריה</Card.Header>
            <Card.Body>
              <div className="row text-center">
                <div className="col-6">
                  <div className="border rounded p-2 bg-success bg-opacity-10">
                    <div className="fw-bold text-success">{summary.consumables_summary.total_consumables - summary.consumables_summary.low_stock_count}</div>
                    <small>מלאי תקין</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="border rounded p-2 bg-danger bg-opacity-10">
                    <div className="fw-bold text-danger">{summary.consumables_summary.low_stock_count}</div>
                    <small>מלאי נמוך</small>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>סה"כ מתכלים:</span>
                  <span className="badge bg-secondary">{summary.consumables_summary.total_consumables}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span>אחוז מלאי נמוך:</span>
                  <span className={`badge ${summary.consumables_summary.low_stock_count > 0 ? 'bg-danger' : 'bg-success'}`}>
                    {summary.consumables_summary.total_consumables > 0 
                      ? Math.round((summary.consumables_summary.low_stock_count / summary.consumables_summary.total_consumables) * 100)
                      : 0}%
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* --- חדש: סטטוס אירועים לפי שבוע --- */}
        <Col md={6} lg={6} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-info text-white">סטטוס אירועים לפי שבוע</Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>אירועים השבוע</h6>
                <span className="badge bg-primary fs-6">
                  {summary.event_summary.events_in_preparation + summary.event_summary.active_events}
                </span>
              </div>
              
              <div className="row text-center">
                <div className="col-4">
                  <div className="border rounded p-2 bg-warning bg-opacity-10">
                    <div className="fw-bold text-warning">{summary.event_summary.events_in_preparation}</div>
                    <small>בהכנה</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-2 bg-success bg-opacity-10">
                    <div className="fw-bold text-success">{summary.event_summary.active_events}</div>
                    <small>פעילים</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="border rounded p-2 bg-secondary bg-opacity-10">
                    <div className="fw-bold text-secondary">{summary.event_summary.completed_events_today}</div>
                    <small>הושלמו</small>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>אירועים קרובים:</span>
                  <span className="badge bg-info">
                    {summary.event_summary.events_in_preparation + summary.event_summary.active_events}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

      </Row>

      {/* מודל להוספת אירוע חדש */}
      <Modal 
        show={showEventFormForNewEvent} 
        onHide={handleNewEventFormCancel} 
        size="lg" 
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>הוספת אירוע חדש</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {initialNewEventDates && (
            <EventForm
              onSuccess={handleNewEventFormSuccess}
              onCancel={handleNewEventFormCancel}
              initialStartDate={initialNewEventDates.startDate}
              initialEndDate={initialNewEventDates.endDate}
            />
          )}
        </Modal.Body>
      </Modal>

      <DefectReportGeneralModal
        show={showDefectReportModal && !isTechnician}
        onHide={() => setShowDefectReportModal(false)}
        onDefectReport={handleDefectReport}
      />
    </div>
  );
};

export default Dashboard;
